/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useMemo } from 'react';

import ExpandablePanel from 'components/ExpandablePanel';
import { InstantCredit } from 'state/instantCredit';
import { Tenant } from 'state/tenant';
import useTranslate from 'hooks/useTranslate/useTranslate';
import { moneyMask, conformToMask } from 'utils/masks';
import styles from './styles';

interface PropTypes {
  account: InstantCredit;
  tenant: Tenant;
  onWidgetClick: (account: InstantCredit) => void;
}

export const displayedCreditLimit = (
  account: InstantCredit,
  tenant: Tenant,
) => {
  const { securityCode, creditLimit, firstPurchaseLimit } = account;
  const { tspDisplay } = tenant;
  const hasVirtualCard = Boolean(securityCode);

  return hasVirtualCard && tspDisplay.temporaryShoppingPassLimit
    ? firstPurchaseLimit
    : !hasVirtualCard && tspDisplay.creditLimit
    ? creditLimit
    : undefined;
};

const AccountSelectionWidget: React.FC<PropTypes> = ({
  account,
  tenant,
  onWidgetClick,
}) => {
  const t = useTranslate();
  const handleClick = useCallback(() => onWidgetClick(account), [
    account,
    onWidgetClick,
  ]);

  const creditLimit = useMemo(() => displayedCreditLimit(account, tenant), [
    account,
    tenant,
  ]);

  if (!account.logo) return null;

  return (
    <button css={styles.widgetContainer} onClick={handleClick}>
      <ExpandablePanel
        css={styles.widget}
        panelName={account.logo}
        title={
          <div>
            <p css={styles.cardName}>
              {t(`accountSelection.selection.${account.logo}.cardName`)}
            </p>
            {creditLimit === null || creditLimit === undefined ? null : (
              <p css={styles.creditLimit} data-testid="creditLimitParagraph">
                {conformToMask(creditLimit.toString(), moneyMask)}
              </p>
            )}
            {creditLimit === null || creditLimit === undefined ? null : (
              <p
                css={styles.availableCredit}
                data-testid="availableCreditParagraph"
              >
                {t('accountSelection.availableCredit')}
              </p>
            )}
          </div>
        }
      />
    </button>
  );
};

export default AccountSelectionWidget;
